import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { tap } from 'rxjs';
import { SearchModel } from 'src/app/models/search';
import { environment } from 'src/environments/environment';
import { ApiCacheService } from '../cache-service/api-cache-service';
import { FileService } from '../file/file-service';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private fileService = inject(FileService);
  private http = inject(HttpClient);
  private baseURL: string = environment.HTTP;
  private cache = inject(ApiCacheService);

  //#region Broadcast
  private apiGetBroadcastDropdown = `${this.baseURL}/Broadcast/GetBroadcastDropdown`;
  private apiGetAudienceDropdown = `${this.baseURL}/Broadcast/GetAudienceDropdown`;
  private apiGetAudienceCategoryDropdown = `${this.baseURL}/Broadcast/GetAudienceCategoryDropdown`;
  private apiGetStatusDropdown = `${this.baseURL}/Broadcast/GetStatusDropdown`;
  private apiBroadcastList = `${this.baseURL}/Broadcast/Get`;
  private GetBroadcastListingRequest = `${this.baseURL}/Broadcast/GetBroadcastListingRequest`;
  private apiGetBroadcastListingApproverRequest = `${this.baseURL}/Broadcast/GetBroadcastListingApproverRequest`;
  private apiBroadcastDetails = `${this.baseURL}/Broadcast/GetById`;
  private apiBroadcastRequestDetails = `${this.baseURL}/Broadcast/GetBroadcastByIdRequest`;
  private apiGetBroadcastRequestApproverDetails = `${this.baseURL}/Broadcast/GetBroadcastByIdRequestApprover`;
  private apiCreateBroadcast = `${this.baseURL}/Broadcast/Create`;
  private apiUpdateBroadcast = `${this.baseURL}/Broadcast/Update`;
  private apiDeleteBroadcast = `${this.baseURL}/Broadcast/Delete`;
  private apiApproveBroadcast = `${this.baseURL}/Broadcast/ApproveBroadcastRequest`;
  private apiBroadcastActivity = `${this.baseURL}/Broadcast/GetActivityLog`;

  getBroadcastDropdown() {
    return this.cache.get(`apiGetBroadcastDropdown`, () => this.http.get<any>(this.apiGetBroadcastDropdown));
  }

  getAudienceDropdown(audienceCategoryId: any) {
    return this.cache.get(`apiGetAudienceDropdown=${audienceCategoryId}`, () =>
      this.http.get<any>(this.apiGetAudienceDropdown, { params: { audienceCategoryId } })
    );
  }

  getAudienceCategoryDropdown() {
    return this.cache.get(`apiGetAudienceCategoryDropdown`, () => this.http.get<any>(this.apiGetAudienceCategoryDropdown));
  }

  getStatusDropdown() {
    return this.cache.get(`apiGetStatusDropdown`, () => this.http.get<any>(this.apiGetStatusDropdown));
  }

  getBroadcastInfoById(id: number) {
    return this.cache.get(`apiBroadcastDetails=${id}`, () => this.http.get<any>(this.apiBroadcastDetails, { params: { id } }));
  }

  getBroadcastReqInfoById(id: number) {
    return this.cache.get(`apiBroadcastRequestDetails=${id}`, () =>
      this.http.get<any>(this.apiBroadcastRequestDetails, { params: { uniqueIdentifier: id } })
    );
  }

  getGetBroadcastRequestApproverDetails(id: number) {
    return this.cache.get(`apiGetBroadcastRequestApproverDetails=${id}`, () =>
      this.http.get<any>(this.apiGetBroadcastRequestApproverDetails, { params: { id } })
    );
  }

  getBroadcastList(data: any) {
    return this.http.post<any>(this.apiBroadcastList, data);
  }

  getMyBroadcastReqList(data: any) {
    return this.http.post<any>(this.GetBroadcastListingRequest, data);
  }

  getGetBroadcastListingApproverRequest(data: any) {
    return this.http.post<any>(this.apiGetBroadcastListingApproverRequest, data);
  }

  createBroadcast(data: any) {
    return this.http.post<any>(this.apiCreateBroadcast, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateBroadcast(data: any) {
    return this.http.post<any>(this.apiUpdateBroadcast, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteBroadcast(data: any) {
    return this.http.post<any>(this.apiDeleteBroadcast, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  approveBroadcast(data: any) {
    return this.http.post(this.apiApproveBroadcast, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getBroadcastActivity(id: any) {
    return this.cache.get(`apiBroadcastActivity=${id}`, () => this.http.get<any>(this.apiBroadcastActivity, { params: { id } }));
  }

  //#region Domain Whitelist
  private apiDomainWhitelistDetails = `${this.baseURL}/DomainWhitelist/GetById`;
  private apiDomainWhitelistList = `${this.baseURL}/DomainWhitelist/Get`;
  private apiCreateDomainWhitelist = `${this.baseURL}/DomainWhitelist/Create`;
  private apiUpdateDomainWhitelist = `${this.baseURL}/DomainWhitelist/Update`;
  private apiDeleteDomainWhitelist = `${this.baseURL}/DomainWhitelist/Delete`;
  private apiDomainWhitelistActivity = `${this.baseURL}/DomainWhitelist/GetActivityLog`;

  getDomainWhitelistInfoById(id: number) {
    return this.cache.get(`apiDomainWhitelistDetails=${id}`, () => this.http.get<any>(this.apiDomainWhitelistDetails, { params: { id } }));
  }

  getDomainWhitelistList(data: any) {
    return this.http.post<any>(this.apiDomainWhitelistList, data);
  }

  createDomainWhitelist(data: any) {
    return this.http.post(this.apiCreateDomainWhitelist, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateDomainWhitelist(data: any) {
    return this.http.post(this.apiUpdateDomainWhitelist, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteDomainWhitelist(data: any) {
    return this.http.post(this.apiDeleteDomainWhitelist, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getDomainWhitelistActivity(id: any) {
    return this.cache.get(`apiDomainWhitelistActivity=${id}`, () =>
      this.http.get<any>(this.apiDomainWhitelistActivity, { params: { id } })
    );
  }

  //#region User Online
  private apiUserOnlineList = `${this.baseURL}/UserOnline/Get`;
  private apiUserOnlineReqList = `${this.baseURL}/UserOnline/GetRequest`;
  private apiUserOnlineReqDetails = `${this.baseURL}/UserOnline/GetByIdRequest`;
  private apiEndUserSession = `${this.baseURL}/UserOnline/EndUserSession`;

  getUserOnlineList(data: any) {
    return this.http.post<any>(this.apiUserOnlineList, data);
  }

  getUserOnlineReqList(data: any) {
    return this.http.post<any>(this.apiUserOnlineReqList, data);
  }

  getUserOnlineReqDetails(uniqueIdentifier: any) {
    return this.cache.get(`apiUserOnlineReqDetails=${uniqueIdentifier}`, () =>
      this.http.get<any>(this.apiUserOnlineReqDetails, { params: { uniqueIdentifier } })
    );
  }

  endUserSession(data: any) {
    return this.http.post(this.apiEndUserSession, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Language Maintenance
  private apiGetLanguage = `${this.baseURL}/LanguageMaintenance/Get`;
  private apiGetLanguageById = `${this.baseURL}/LanguageMaintenance/GetById`;
  private apiUpdateLanguage = `${this.baseURL}/LanguageMaintenance/Update`;
  private apiUpdateStatusLanguage = `${this.baseURL}/LanguageMaintenance/UpdateMasLanguage`;
  private apiGetLanguagePackage = `${this.baseURL}/LanguageMaintenance/GetLanguagePackage`;
  private apiDownloadTemplate = `${this.baseURL}/LanguageMaintenance/DownloadTemplate`;
  private apiExportLanguage = `${this.baseURL}/LanguageMaintenance/Export`;
  private apiImportLanguage = `${this.baseURL}/LanguageMaintenance/Import`;

  GetLanguage(data: any) {
    return this.http.post<any>(this.apiGetLanguage, data);
  }

  GetLanguageById(data: any) {
    return this.http.post(this.apiGetLanguageById, data);
  }

  GetLanguagePackage(id: any) {
    return this.cache.get(`apiGetLanguagePackage=${id}`, () =>
      this.http.get<any>(this.apiGetLanguagePackage, { params: { languageId: id } })
    );
  }

  DownloadTemplate() {
    return this.http.get(this.apiDownloadTemplate, { responseType: 'blob' }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  ExportLanguage(data: any) {
    return this.http.post(this.apiExportLanguage, data, { responseType: 'blob', observe: 'response' }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  ImportLanguage(formData: FormData) {
    return this.http.post<any>(this.apiImportLanguage, formData, { reportProgress: true, observe: 'events' }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  UpdateLanguage(data: any) {
    return this.http.post(this.apiUpdateLanguage, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateStatusLanguage(data: any) {
    return this.http.post(this.apiUpdateStatusLanguage, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Country
  private apiCountryList = `${this.baseURL}/Country/GetCountryListing`;
  private apiCountryDropdown = `${this.baseURL}/Entity/GetCountryDropdown`;
  private apiGetCountryDefaultCurrency = `${this.baseURL}/Country/GetCountryDefaultCurrency`;

  getCountryList(data: SearchModel) {
    return this.http.post<any>(this.apiCountryList, data);
  }

  getCountryDropdown() {
    return this.cache.get(`apiCountryDropdown`, () => this.http.get<any>(this.apiCountryDropdown));
  }

  getCountryDefaultCurrency(countryId: number) {
    return this.cache.get(`apiGetCountryDefaultCurrency=${countryId}`, () =>
      this.http.get<any>(this.apiGetCountryDefaultCurrency, { params: { countryId } })
    );
  }

  //#region State
  private apiStateDetails = `${this.baseURL}/State/GetById`;
  private apiStateList = `${this.baseURL}/State/Get`;
  private apiCreateState = `${this.baseURL}/State/Create`;
  private apiUpdateState = `${this.baseURL}/State/Update`;
  private apiDeleteState = `${this.baseURL}/State/Delete`;
  private apiStateActivity = `${this.baseURL}/State/GetActivityLog`;

  getStateInfoById(id: number) {
    return this.cache.get(`apiStateDetails=${id}`, () => this.http.get<any>(this.apiStateDetails, { params: { id } }));
  }

  getStateList(data: SearchModel) {
    return this.http.post<any>(this.apiStateList, data);
  }

  createState(data: any) {
    return this.http.post(this.apiCreateState, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateState(data: any) {
    return this.http.post(this.apiUpdateState, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteState(data: any) {
    return this.http.post(this.apiDeleteState, data);
  }

  getStateActivity(id: any) {
    return this.cache.get(`apiStateActivity=${id}`, () => this.http.get<any>(this.apiStateActivity, { params: { id } }));
  }

  //#region Region
  private apiRegionDetails = `${this.baseURL}/Region/GetById`;
  private apiRegionList = `${this.baseURL}/Region/Get`;
  private apiRegionDropdown = `${this.baseURL}/Region/GetRegionDropdown`;
  private apiCreateRegion = `${this.baseURL}/Region/Create`;
  private apiUpdateRegion = `${this.baseURL}/Region/Update`;
  private apiDeleteRegion = `${this.baseURL}/Region/Delete`;
  private apiRegionActivity = `${this.baseURL}/Region/GetActivityLog`;

  getRegionInfoById(id: number) {
    return this.cache.get(`apiRegionDetails=${id}`, () => this.http.get<any>(this.apiRegionDetails, { params: { id } }));
  }

  getRegionList(data: SearchModel) {
    return this.http.post<any>(this.apiRegionList, data);
  }

  createRegion(data: any) {
    return this.http.post(this.apiCreateRegion, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateRegion(data: any) {
    return this.http.post(this.apiUpdateRegion, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteRegion(data: any) {
    return this.http.post(this.apiDeleteRegion, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getRegionActivity(id: number) {
    return this.cache.get(`apiRegionActivity=${id}`, () => this.http.get<any>(this.apiRegionActivity, { params: { id } }));
  }

  GetRegionDropdown(id: number) {
    return this.http.get<any>(this.apiRegionDropdown, { params: { countryId: id } });
  }

  //#region Document Number Series
  private apiDocumentNumberSeriesList = `${this.baseURL}/DocumentNumberSeries/Get`;
  private apiDocumentNumberSeriesRequestList = `${this.baseURL}/DocumentNumberSeries/GetRequest`;
  private apiDocumentNumberSeriesApprovalList = `${this.baseURL}/DocumentNumberSeries/GetRequestApprover`;
  private apiDocumentNumberSeriesDetails = `${this.baseURL}/DocumentNumberSeries/GetById`;
  private apiDocumentNumberSerieRequestDetails = `${this.baseURL}/DocumentNumberSeries/GetByIdRequest`;
  private apiDocumentNumberSerieApproverDetails = `${this.baseURL}/DocumentNumberSeries/GetByIdRequestApprover`;
  private apiCreateDocumentNumberSeries = `${this.baseURL}/DocumentNumberSeries/Create`;
  private apiUpdateDocumentNumberSeries = `${this.baseURL}/DocumentNumberSeries/Update`;
  private apiDeleteDocumentNumberSeries = `${this.baseURL}/DocumentNumberSeries/Delete`;
  private apiValidateDocumentNumberSeries = `${this.baseURL}/DocumentNumberSeries/Validate`;
  private apiGetDocumentType = `${this.baseURL}/DocumentNumberSeries/GetDocumentType`;
  private apiGetDocumentTypeForFilter = `${this.baseURL}/DocumentNumberSeries/GetDocumentTypeForFilter`;
  private apiDocumentNumberSeriesActivity = `${this.baseURL}/DocumentNumberSeries/GetActivityLog`;
  private apiApproveDocumentNumberSeries = `${this.baseURL}/DocumentNumberSeries/ApproveRequest`;
  private apiDocumentNumberSeriesHistoryList = `${this.baseURL}/DocumentNumberSeries/GetHistory`;
  private apiGetNumberSeriesLocalizationByEntity = `${this.baseURL}/DocumentNumberSeries/GetNumberSeriesLocalizationByEntity`;

  getDocumentNumberSeriesInfoById(id: number) {
    return this.cache.get(`apiDocumentNumberSeriesDetails=${id}`, () =>
      this.http.get<any>(this.apiDocumentNumberSeriesDetails, { params: { id } })
    );
  }

  getDocumentNumberSeriesRequestInfoById(uniqueIdentifier: number) {
    return this.cache.get(`apiDocumentNumberSerieRequestDetails=${uniqueIdentifier}`, () =>
      this.http.get<any>(this.apiDocumentNumberSerieRequestDetails, { params: { uniqueIdentifier } })
    );
  }

  getDocumentNumberSeriesApproverInfoById(id: number) {
    return this.cache.get(`apiDocumentNumberSerieApproverDetails=${id}`, () =>
      this.http.get<any>(this.apiDocumentNumberSerieApproverDetails, { params: { id } })
    );
  }

  getDocumentNumberSeriesList(data: SearchModel) {
    return this.http.post<any>(this.apiDocumentNumberSeriesList, data);
  }

  getNumberSeriesLocalizationByEntity(numberSeriesId: number, countryScopeId: number, data: SearchModel) {
    return this.http.post<any>(
      `${this.apiGetNumberSeriesLocalizationByEntity}?numberSeriesId=${numberSeriesId}&countryScopeId=${countryScopeId}`,
      data
    );
  }

  getDocumentNumberSeriesHistoryList(docTypeId: string) {
    return this.cache.get(`apiDocumentNumberSeriesHistoryList=${docTypeId}`, () =>
      this.http.get<any>(this.apiDocumentNumberSeriesHistoryList, { params: { docTypeId } })
    );
  }

  getDocumentNumberSeriesRequestList(data: SearchModel) {
    return this.http.post<any>(this.apiDocumentNumberSeriesRequestList, data);
  }

  getDocumentNumberSeriesApprovalList(data: SearchModel) {
    return this.http.post<any>(this.apiDocumentNumberSeriesApprovalList, data);
  }

  createDocumentNumberSeries(data: any) {
    return this.http.post(this.apiCreateDocumentNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateDocumentNumberSeries(data: any) {
    return this.http.post(this.apiUpdateDocumentNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteDocumentNumberSeries(data: any) {
    return this.http.post(this.apiDeleteDocumentNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  approveDocumentNumberSeries(data: SearchModel) {
    return this.http.post<any>(this.apiApproveDocumentNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  validateDocumentNumberSeries(data: any) {
    return this.http.post(this.apiValidateDocumentNumberSeries, data);
  }

  getDocumentType(categoryId: number, numberSeriesId?: number | null) {
    return this.cache.get(`apiGetDocumentType=${categoryId}-${numberSeriesId ?? 'null'}`, () =>
      this.http.get<any>(this.apiGetDocumentType, {
        params: { categoryId, ...(numberSeriesId ? { numberSeriesId } : {}) }
      })
    );
  }

  getDocumentTypeForFilter(categoryId: number) {
    return this.cache.get(`apiGetDocumentTypeForFilter=${categoryId}`, () =>
      this.http.get<any>(this.apiGetDocumentTypeForFilter, { params: { categoryId } })
    );
  }

  getDocumentNumberSeriesActivity(id: number) {
    return this.cache.get(`apiDocumentNumberSeriesActivity=${id}`, () =>
      this.http.get<any>(this.apiDocumentNumberSeriesActivity, { params: { id } })
    );
  }
  //end

  //#region Reason Maintenance
  private apiReasonMaintenanceDetails = `${this.baseURL}/ReasonMaintenance/GetById`;
  private apiReasonMaintenanceList = `${this.baseURL}/ReasonMaintenance/Get`;
  private apiCreateReasonMaintenance = `${this.baseURL}/ReasonMaintenance/Create`;
  private apiUpdateReasonMaintenance = `${this.baseURL}/ReasonMaintenance/Update`;
  private apiDeleteReasonMaintenance = `${this.baseURL}/ReasonMaintenance/Delete`;
  private apiLinkedModuleDropdown = `${this.baseURL}/ReasonMaintenance/GetLinkedModuleDropdown`;
  private apiGetReasonDropdown = `${this.baseURL}/ReasonMaintenance/GetReasonDropdown`;
  private apiReasonMaintenanceActivity = `${this.baseURL}/ReasonMaintenance/GetActivityLog`;

  getReasonMaintenanceInfoById(id: number) {
    return this.cache.get(`apiReasonMaintenanceDetails=${id}`, () =>
      this.http.get<any>(this.apiReasonMaintenanceDetails, { params: { id } })
    );
  }

  getReasonMaintenanceList(data: SearchModel) {
    return this.http.post<any>(this.apiReasonMaintenanceList, data);
  }

  createReasonMaintenance(data: any) {
    return this.http.post(this.apiCreateReasonMaintenance, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateReasonMaintenance(data: any) {
    return this.http.post(this.apiUpdateReasonMaintenance, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteReasonMaintenance(data: any) {
    return this.http.post(this.apiDeleteReasonMaintenance, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getLinkedModuleDropdown() {
    return this.cache.get(`apiLinkedModuleDropdown`, () => this.http.get(this.apiLinkedModuleDropdown));
  }

  getReasonDropdown(code: string) {
    const codeUppercase = code.toUpperCase();
    return this.cache.get(`apiGetReasonDropdown=${codeUppercase}`, () =>
      this.http.get(this.apiGetReasonDropdown, { params: { code: codeUppercase } })
    );
  }

  getReasonMaintenanceActivity(id: any) {
    return this.cache.get(`apiReasonMaintenanceActivity=${id}`, () =>
      this.http.get<any>(this.apiReasonMaintenanceActivity, { params: { id } })
    );
  }

  //#region Document Preset
  private apiDocumentPresetDropdown = `${this.baseURL}/DocumentPreset/GetDropdown`;
  private apiDocumentPresetList = `${this.baseURL}/DocumentPreset/Get`;
  private apiDocumentPresetDetails = `${this.baseURL}/DocumentPreset/GetById`;
  private apiGetProcurementById = `${this.baseURL}/DocumentPreset/GetProcurement`;
  private apiGetIntercompanyById = `${this.baseURL}/DocumentPreset/GetIntercompany`;
  private apiGetInventoryById = `${this.baseURL}/DocumentPreset/GetInventory`;
  private apiGetDocumentTemplateById = `${this.baseURL}/DocumentPreset/GetDocumentTemplate`;
  private apiCreateDocumentPreset = `${this.baseURL}/DocumentPreset/Create`;
  private apiUpdateDocumentPreset = `${this.baseURL}/DocumentPreset/Update`;
  private apiUpsertProcurement = `${this.baseURL}/DocumentPreset/UpsertProcurement`;
  private apiUpsertIntercompany = `${this.baseURL}/DocumentPreset/UpsertIntercompany`;
  private apiUpsertInventory = `${this.baseURL}/DocumentPreset/UpsertInventory`;
  private apiDocumentPresetActivity = `${this.baseURL}/DocumentPreset/GetActivityLog`;

  getDocumentPresetDropdown(type: string) {
    return this.cache.get(`apiDocumentPresetDropdown=${type}`, () => this.http.get(this.apiDocumentPresetDropdown, { params: { type } }));
  }

  getDocumentPresetList(data: any) {
    return this.http.post<any>(this.apiDocumentPresetList, data);
  }

  getDocumentPresetInfoById(id: number) {
    return this.cache.get(`apiDocumentPresetDetails=${id}`, () => this.http.get(this.apiDocumentPresetDetails, { params: { id } }));
  }

  getDocumentPresetProcurementById(scopeId: number) {
    return this.cache.get(`apiGetProcurementById=${scopeId}`, () => this.http.get(this.apiGetProcurementById, { params: { scopeId } }));
  }

  getDocumentPresetIntercompanyById(scopeId: number) {
    return this.cache.get(`apiGetIntercompanyById=${scopeId}`, () => this.http.get(this.apiGetIntercompanyById, { params: { scopeId } }));
  }

  getDocumentPresetInventoryById(scopeId: number) {
    return this.cache.get(`apiGetInventoryById=${scopeId}`, () => this.http.get(this.apiGetInventoryById, { params: { scopeId } }));
  }

  getDocumentTemplateById(documentid: any) {
    return this.cache.get(`apiGetDocumentTemplateById=${documentid}`, () =>
      this.http.get(this.apiGetDocumentTemplateById, { params: { documentid } })
    );
  }

  createDocumentPreset(data: any) {
    return this.http.post(this.apiCreateDocumentPreset, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateDocumentPreset(data: any) {
    return this.http.post(this.apiUpdateDocumentPreset, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  upsertProcurement(data: any) {
    return this.http.post(this.apiUpsertProcurement, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  upsertIntercompany(data: any) {
    return this.http.post(this.apiUpsertIntercompany, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  upsertInventory(data: any) {
    return this.http.post(this.apiUpsertInventory, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getDocumentPresetActivity(id: number) {
    return this.cache.get(`apiDocumentPresetActivity=${id}`, () => this.http.get<any>(this.apiDocumentPresetActivity, { params: { id } }));
  }

  //#region Document Template
  private apiProcurementList = `${this.baseURL}/DocumentTemplate/GetProcurementList`;
  private apiIntercompanyList = `${this.baseURL}/DocumentTemplate/GetIntercompanyList`;
  private apiInventoryList = `${this.baseURL}/DocumentTemplate/GetInventoryList`;
  private apiDocumentTemplateDetails = `${this.baseURL}/DocumentTemplate/GetById`;
  private apiCreateDocumentTemplate = `${this.baseURL}/DocumentTemplate/Create`;
  private apiUpdateDocumentTemplate = `${this.baseURL}/DocumentTemplate/Update`;
  private apiDeleteDocumentTemplate = `${this.baseURL}/DocumentTemplate/Delete`;

  getProcurementList(data: any) {
    return this.http.post<any>(this.apiProcurementList, data);
  }

  getIntercompanyList(data: any) {
    return this.http.post<any>(this.apiIntercompanyList, data);
  }

  getInventoryList(data: any) {
    return this.http.post<any>(this.apiInventoryList, data);
  }

  getDocumentTemplateInfoById(id: number) {
    return this.cache.get(`apiDocumentTemplateDetails=${id}`, () =>
      this.http.get<any>(this.apiDocumentTemplateDetails, { params: { id } })
    );
  }

  //#region Payment Method
  private apiPaymentMethod = `${this.baseURL}/PaymentMethod/GetPaymentMethodDropdown`;
  private apiPaymentMethodList = `${this.baseURL}/PaymentMethod/Get`;
  private apiPaymentMethodDetails = `${this.baseURL}/PaymentMethod/GetById`;
  private apiCreatePaymentMethod = `${this.baseURL}/PaymentMethod/Create`;
  private apiUpdatePaymentMethod = `${this.baseURL}/PaymentMethod/Update`;
  private apiDeletePaymentMethod = `${this.baseURL}/PaymentMethod/Delete`;
  private apiPaymentMethodActivity = `${this.baseURL}/PaymentMethod/GetActivityLog`;

  GetPaymentMethodDropdown() {
    return this.cache.get(`apiPaymentMethod`, () => this.http.get<any>(this.apiPaymentMethod));
  }

  getPaymentMethodList(data: any) {
    return this.http.post<any>(this.apiPaymentMethodList, data);
  }

  getPaymentMethodInfoById(id: number) {
    return this.cache.get(`apiPaymentMethodDetails=${id}`, () => this.http.get<any>(this.apiPaymentMethodDetails, { params: { id } }));
  }

  createPaymentMethod(data: any) {
    return this.http.post(this.apiCreatePaymentMethod, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updatePaymentMethod(data: any) {
    return this.http.post(this.apiUpdatePaymentMethod, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deletePaymentMethod(data: any) {
    return this.http.post(this.apiDeletePaymentMethod, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getPaymentMethodActivity(id: number) {
    return this.cache.get(`apiPaymentMethodActivity=${id}`, () => this.http.get<any>(this.apiPaymentMethodActivity, { params: { id } }));
  }

  //#region KYB Document Type
  private apiKYBDocumentTypeList = `${this.baseURL}/KybDocument/Get`;
  private apiKYBDocumentTypeDetails = `${this.baseURL}/KybDocument/GetById`;
  private apiCreateKYBDocumentType = `${this.baseURL}/KybDocument/Create`;
  private apiUpdateKYBDocumentType = `${this.baseURL}/KybDocument/Update`;
  private apiDeleteKYBDocumentType = `${this.baseURL}/KybDocument/Delete`;
  private apiKYBDocumentTypeActivity = `${this.baseURL}/KybDocument/GetActivityLog`;

  getKYBDocumentTypeList(data: any) {
    return this.http.post<any>(this.apiKYBDocumentTypeList, data);
  }

  getKYBDocumentTypeInfoById(id: number) {
    return this.cache.get(`apiKYBDocumentTypeDetails=${id}`, () => this.http.get<any>(this.apiKYBDocumentTypeDetails, { params: { id } }));
  }

  createKYBDocumentType(data: any) {
    return this.http.post(this.apiCreateKYBDocumentType, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateKYBDocumentType(data: any) {
    return this.http.post(this.apiUpdateKYBDocumentType, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteKYBDocumentType(data: any) {
    return this.http.post(this.apiDeleteKYBDocumentType, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getKYBDocumentTypeActivity(id: number) {
    return this.cache.get(`apiKYBDocumentTypeActivity=${id}`, () =>
      this.http.get<any>(this.apiKYBDocumentTypeActivity, { params: { id } })
    );
  }

  //#region Bank
  private apiBankList = `${this.baseURL}/BankListing/Get`;
  private apiBankDetails = `${this.baseURL}/BankListing/GetById`;
  private apiCreateBank = `${this.baseURL}/BankListing/Create`;
  private apiUpdateBank = `${this.baseURL}/BankListing/Update`;
  private apiDeleteBank = `${this.baseURL}/BankListing/Delete`;
  private apiBankActivity = `${this.baseURL}/BankListing/GetActivityLog`;
  private apiParentBankDropdown = `${this.baseURL}/BankListing/GetParentDropdown`;
  private apiCurrencyDropdown = `${this.baseURL}/BankListing/GetCurrency`;

  getBankList(data: any) {
    return this.http.post<any>(this.apiBankList, data);
  }

  getBankInfoById(id: number) {
    return this.cache.get(`apiBankDetails=${id}`, () => this.http.get<any>(this.apiBankDetails, { params: { id } }));
  }

  createBank(data: any) {
    return this.http.post(this.apiCreateBank, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateBank(data: any) {
    return this.http.post(this.apiUpdateBank, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteBank(data: any) {
    return this.http.post(this.apiDeleteBank, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getBankActivity(id: number) {
    return this.cache.get(`apiBankActivity=${id}`, () => this.http.get<any>(this.apiBankActivity, { params: { id } }));
  }

  getParentBankDropdown() {
    return this.cache.get(`apiParentBankDropdown`, () => this.http.get<any>(this.apiParentBankDropdown));
  }

  getCurrencyDropdown(id: number) {
    return this.cache.get(`apiCurrencyDropdown=${id}`, () => this.http.get<any>(this.apiCurrencyDropdown, { params: { countryId: id } }));
  }

  // #region Localization Maintenance
  private apiGetLocalizationMaintenance = `${this.baseURL}/LocalizationMaintenance/Get`;
  private apiGetActivityLogLocalizationMaintenance = `${this.baseURL}/LocalizationMaintenance/GetActivityLog`;
  private apiGetLocalizationGeneralSetup = `${this.baseURL}/LocalizationMaintenance/GeneralSetup/Get`;
  private apiCreateLocalizationGeneralSetup = `${this.baseURL}/LocalizationMaintenance/GeneralSetup/Create`;
  private apiUpdateLocalizationGeneralSetup = `${this.baseURL}/LocalizationMaintenance/GeneralSetup/Update`;
  private apiSuspendLocalizationGeneralSetup = `${this.baseURL}/LocalizationMaintenance/GeneralSetup/Suspend`;
  private apiReactiveLocalizationGeneralSetup = `${this.baseURL}/LocalizationMaintenance/GeneralSetup/Reactivate`;
  private apiGetLocalizationTaxationById = `${this.baseURL}/LocalizationMaintenance/Taxation/GetById`;
  private apiUpdateLocalizationTaxation = `${this.baseURL}/LocalizationMaintenance/Taxation/Update`;
  private apiGetLocalizationKybDocument = `${this.baseURL}/LocalizationMaintenance/KybDocument/Get`;
  private apiUpdateLocalizationKybDocument = `${this.baseURL}/LocalizationMaintenance/KybDocument/Update`;
  private apiGetLocalizationNumberSeries = `${this.baseURL}/LocalizationMaintenance/NumberSeries/Get`;
  private apiGetLocalizationDocumentPreset = `${this.baseURL}/LocalizationMaintenance/DocumentPreset/Get`;
  private apiUpdateLocalizationDocumentPreset = `${this.baseURL}/LocalizationMaintenance/DocumentPreset/Update`;
  private apiUnusedCountryDropdown = `${this.baseURL}/Country/GetUnusedCountryDropdown`;
  private apiGetCountryDetailById = `${this.baseURL}/Country/GetCountryDetailById`;

  getLocalizationMaintenanceList(data?: any) {
    return this.http.post(this.apiGetLocalizationMaintenance, data);
  }

  getLocalizationMaintenanceActivityLog(id: number) {
    return this.cache.get(`apiGetActivityLogLocalizationMaintenance=${id}`, () =>
      this.http.get<any>(this.apiGetActivityLogLocalizationMaintenance, { params: { id } })
    );
  }

  getLocalizationGeneralSetup(id: number) {
    return this.cache.get(`apiGetLocalizationGeneralSetup=${id}`, () =>
      this.http.get<any>(this.apiGetLocalizationGeneralSetup, { params: { masScopeId: id } })
    );
  }

  createLocalizationGeneralSetup(data: any) {
    return this.http.post(this.apiCreateLocalizationGeneralSetup, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateLocalizationGeneralSetup(data: any) {
    return this.http.post(this.apiUpdateLocalizationGeneralSetup, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getLocalizationTaxationById(id: number) {
    return this.cache.get(`apiGetLocalizationTaxationById=${id}`, () =>
      this.http.get<any>(this.apiGetLocalizationTaxationById, { params: { countryId: id } })
    );
  }

  updateLocalizationTaxation(data: any) {
    return this.http.post(this.apiUpdateLocalizationTaxation, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getLocalizationKybDocument(id: number) {
    return this.cache.get(`apiGetLocalizationKybDocument=${id}`, () =>
      this.http.get<any>(this.apiGetLocalizationKybDocument, { params: { countryId: id } })
    );
  }

  updateLocalizationKybDocument(data: any) {
    return this.http.post(this.apiUpdateLocalizationKybDocument, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getLocalizationNumberSeries(data: any) {
    return this.http.post(this.apiGetLocalizationNumberSeries, data);
  }

  getLocalizationDocumentPreset(id: number) {
    return this.cache.get(`apiGetLocalizationDocumentPreset=${id}`, () =>
      this.http.get<any>(this.apiGetLocalizationDocumentPreset, { params: { scopeId: id } })
    );
  }

  updateLocalizationDocumentPreset(data: any) {
    return this.http.post(this.apiUpdateLocalizationDocumentPreset, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getUnusedCountryDropdown() {
    return this.cache.get(`apiUnusedCountryDropdown`, () => this.http.post<any>(this.apiUnusedCountryDropdown, null));
  }

  getCountryDetailsById(id: number) {
    return this.cache.get(`apiGetCountryDetailById=${id}`, () => this.http.get<any>(this.apiGetCountryDetailById, { params: { id } }));
  }

  suspendLocalizationGeneralSetup(data: number[]) {
    return this.http.post(this.apiSuspendLocalizationGeneralSetup, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  reactiveLocalizationGeneralSetup(data: number[]) {
    return this.http.post(this.apiReactiveLocalizationGeneralSetup, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Licensing
  private apiGetGlobalLicense = `${this.baseURL}/UserOnline/GetGlobalLicenceUsage`;
  private apiGetCountryLicense = `${this.baseURL}/UserOnline/GetCountryLicenseUsage`;
  private apiGetStoreLicense = `${this.baseURL}/UserOnline/GetStoreLicenseUsage`;
  private apiAddGlobalLicense = `${this.baseURL}/UserOnline/AddGlobalLicenseQuantity`;
  private apiAddStoreLicense = `${this.baseURL}/UserOnline/AddStoreLicenseQuantity`;
  private apiAddCountryLicense = `${this.baseURL}/UserOnline/AddCountryLicenseQuantity`;
  private apiGetPermittedCountryDropdown = `${this.baseURL}/UserOnline/GetPermittedCountryDropdown`;
  private apiGetLicenseReq = `${this.baseURL}/LicenseAdjustment/GetLicenseRequest`;
  private apiGetDeviceReq = `${this.baseURL}/LicenseAdjustment/GetDeviceRequest`;
  private apiApproveLicenseAdjustment = `${this.baseURL}/LicenseAdjustment/ApproveLicenseRequestAsync`;
  private apiRejectLicenseAdjustment = `${this.baseURL}/LicenseAdjustment/RejectLicenseRequestAsync`;
  private apiRevertLicenseAdjustment = `${this.baseURL}/LicenseAdjustment/RevertLicenseRequestAsync `;
  private apiGetLicenseType = `${this.baseURL}/LicenseAdjustment/GetLicenseTypeDropdown`;
  private apiGetUserOnlineByIdRequestApprover = `${this.baseURL}/LicenseAdjustment/GetUserOnlineByIdRequestApprover`;
  private apiGetDeviceLicenseByIdRequestApprover = `${this.baseURL}/LicenseAdjustment/GetDeviceLicenseByIdRequestApprover`;

  getLicenseTypeDropdown() {
    return this.cache.get(`apiGetLicenseType`, () => this.http.get<any>(this.apiGetLicenseType));
  }

  getGlobalLicense() {
    return this.cache.get(`apiGetGlobalLicense`, () => this.http.get<any>(this.apiGetGlobalLicense));
  }

  getCountryLicense(countryId: number) {
    return this.cache.get(`apiGetCountryLicense=${countryId}`, () =>
      this.http.get<any>(this.apiGetCountryLicense, { params: { countryId } })
    );
  }

  getStoreLicense() {
    return this.cache.get(`apiGetStoreLicense`, () => this.http.get<any>(this.apiGetStoreLicense));
  }

  getPermittedCountryDropdown() {
    return this.cache.get(`apiGetPermittedCountryDropdown`, () => this.http.get<any>(this.apiGetPermittedCountryDropdown));
  }

  addGlobalLicense(data: any) {
    return this.http.post(this.apiAddGlobalLicense, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  addCountryLicense(data: any) {
    return this.http.post(this.apiAddCountryLicense, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }
  addStoreLicense(data: any) {
    return this.http.post(this.apiAddStoreLicense, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getLicenseRequest(data: any) {
    return this.http.post(this.apiGetLicenseReq, data);
  }

  getDeviceRequest(data: any) {
    return this.http.post(this.apiGetDeviceReq, data);
  }

  getUserOnlineByIdRequestApprover(uniqueIdentifier: any) {
    return this.cache.get(`apiGetUserOnlineByIdRequestApprover=${uniqueIdentifier}`, () =>
      this.http.get<any>(this.apiGetUserOnlineByIdRequestApprover, { params: { uniqueIdentifier } })
    );
  }

  getDeviceLicenseByIdRequestApprover(uniqueIdentifier: any) {
    return this.cache.get(`apiGetDeviceLicenseByIdRequestApprover=${uniqueIdentifier}`, () =>
      this.http.get<any>(this.apiGetDeviceLicenseByIdRequestApprover, { params: { uniqueIdentifier } })
    );
  }

  approveLicenseAdjustment(data: any) {
    return this.http.post(this.apiApproveLicenseAdjustment, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  rejectLicenseAdjustment(data: any) {
    return this.http.post(this.apiRejectLicenseAdjustment, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  revertLicenseAdjustment(data: any) {
    return this.http.post(this.apiRevertLicenseAdjustment, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Device License
  private apiDeviceLicenseUsage = `${this.baseURL}/DeviceLicense/GetDeviceLicenseUsage`;
  private apiAddDeviceLicenseQuantity = `${this.baseURL}/DeviceLicense/AddDeviceLicenseQuantity`;
  private apiGetDeviceLicense = `${this.baseURL}/DeviceLicense/Get`;
  private apiGetDeviceLicenseReq = `${this.baseURL}/DeviceLicense/GetRequest`;
  private apiGetDeviceLicenseReqDetails = `${this.baseURL}/DeviceLicense/GetByIdRequest`;

  getDeviceLicenseUsage() {
    return this.cache.get(`apiDeviceLicenseUsage`, () => this.http.get<any>(this.apiDeviceLicenseUsage));
  }

  addDeviceLicenseQuantity(data: any) {
    return this.http.post(this.apiAddDeviceLicenseQuantity, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getDeviceLicense(data: any) {
    return this.http.post(this.apiGetDeviceLicense, data);
  }

  getDeviceLicenseReqList(data: any) {
    return this.http.post(this.apiGetDeviceLicenseReq, data);
  }

  getDeviceLicenseReqDetails(uniqueIdentifier: any) {
    return this.cache.get(`apiGetDeviceLicenseReqDetails=${uniqueIdentifier}`, () =>
      this.http.get<any>(this.apiGetDeviceLicenseReqDetails, { params: { uniqueIdentifier } })
    );
  }

  //#region MSIC
  private apiGetMSICDropdown = `${this.baseURL}/Msic/GetMsicDropdown`;
  private apiMsicDetails = `${this.baseURL}/Msic/GetById`;
  private apiMsicList = `${this.baseURL}/Msic/Get`;
  private apiCreateMsic = `${this.baseURL}/Msic/Create`;
  private apiUpdateMsic = `${this.baseURL}/Msic/Update`;
  private apiDeleteMsic = `${this.baseURL}/Msic/Delete`;
  private apiDuplicateMsic = `${this.baseURL}/Msic/Duplicate`;
  private apiMsicActivity = `${this.baseURL}/Msic/GetActivityLog`;

  getMSICDropdown() {
    return this.cache.get(`apiGetMSICDropdown`, () => this.http.get<any>(this.apiGetMSICDropdown));
  }

  getMsicInfoById(id: number) {
    return this.cache.get(`apiMsicDetails=${id}`, () => this.http.get(this.apiMsicDetails, { params: { id } }));
  }

  getMsicList(data: any) {
    return this.http.post<any>(this.apiMsicList, data);
  }

  createMsic(data: any) {
    return this.http.post(this.apiCreateMsic, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateMsic(data: any) {
    return this.http.post(this.apiUpdateMsic, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  deleteMsic(data: any) {
    return this.http.post(this.apiDeleteMsic, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  duplicateMsic(id: any) {
    return this.http.post(this.apiDuplicateMsic, null, { params: { id: id } }).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getMsicActivity(id: number) {
    return this.cache.get(`apiMsicActivity`, () => this.http.get<any>(this.apiMsicActivity, { params: { id } }));
  }

  //#region Number Series
  private apiGeneralNumberSeriesList = `${this.baseURL}/GeneralNumberSeries/Get`;
  private apiGeneralNumberSeriesRequestList = `${this.baseURL}/GeneralNumberSeries/GetRequest`;
  private apiGeneralNumberSeriesDetails = `${this.baseURL}/GeneralNumberSeries/GetById`;
  private apiGeneralNumberSeriesHistoryList = `${this.baseURL}/GeneralNumberSeries/GetHistory`;
  private apiGeneralNumberSeriesRequestDetails = `${this.baseURL}/GeneralNumberSeries/GetByIdRequest`;
  private apiGeneralNumberSeriesApprovalDetails = `${this.baseURL}/GeneralNumberSeries/GetByIdRequestApprover`;
  private apiCreateGeneralNumberSeries = `${this.baseURL}/GeneralNumberSeries/Create`;
  private apiUpdateGeneralNumberSeries = `${this.baseURL}/GeneralNumberSeries/Update`;
  private apiGetGeneralNumberSeriesActivityLog = `${this.baseURL}/GeneralNumberSeries/GetActivityLog`;
  private apiGetNumberSeriesModuleDropdown = `${this.baseURL}/GeneralNumberSeries/GetNumberSeriesModuleDropdown`;
  private apiSaveGeneralNumberSeries = `${this.baseURL}/GeneralNumberSeries/Save`;
  private apiGeneralNumberSeriesApprovalRequestList = `${this.baseURL}/GeneralNumberSeries/GetRequestApprover`;
  private apiApproveGeneralNumberSeries = `${this.baseURL}/GeneralNumberSeries/ApproveRequest`;

  getGeneralNumberSeriesInfoById(id: number) {
    return this.cache.get(`apiGeneralNumberSeriesDetails=${id}`, () =>
      this.http.get<any>(this.apiGeneralNumberSeriesDetails, { params: { id } })
    );
  }

  getGeneralNumberSeriesHistoryList(typeCode: string) {
    return this.cache.get(`apiGeneralNumberSeriesHistoryList=${typeCode}`, () =>
      this.http.get<any>(this.apiGeneralNumberSeriesHistoryList, { params: { typeCode } })
    );
  }

  getGeneralNumberSeriesInfoRequestById(uniqueIdentifier: string) {
    return this.cache.get(`apiGeneralNumberSeriesRequestDetails=${uniqueIdentifier}`, () =>
      this.http.get<any>(this.apiGeneralNumberSeriesRequestDetails, { params: { uniqueIdentifier } })
    );
  }

  getGeneralNumberSeriesInfoApprovalById(id: number) {
    return this.cache.get(`apiGeneralNumberSeriesApprovalDetails=${id}`, () =>
      this.http.get<any>(this.apiGeneralNumberSeriesApprovalDetails, { params: { id } })
    );
  }

  getGeneralNumberSeriesList(data: SearchModel) {
    return this.http.post<any>(this.apiGeneralNumberSeriesList, data);
  }

  getGeneralNumberSeriesRequestList(data: SearchModel) {
    return this.http.post<any>(this.apiGeneralNumberSeriesRequestList, data);
  }

  getGeneralNumberSeriesApprovalRequestList(data: SearchModel) {
    return this.http.post<any>(this.apiGeneralNumberSeriesApprovalRequestList, data);
  }

  approveGeneralNumberSeries(data: SearchModel) {
    return this.http.post<any>(this.apiApproveGeneralNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  createGeneralNumberSeries(data: any) {
    return this.http.post(this.apiCreateGeneralNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateGeneralNumberSeries(data: any) {
    return this.http.post(this.apiUpdateGeneralNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getGeneralNumberSeriesActivityLog(id: number) {
    return this.cache.get(`apiGetGeneralNumberSeriesActivityLog=${id}`, () =>
      this.http.get<any>(this.apiGetGeneralNumberSeriesActivityLog, { params: { id } })
    );
  }

  getNumberSeriesModuleDropdown() {
    return this.cache.get(`apiGetNumberSeriesModuleDropdown`, () => this.http.get<any>(this.apiGetNumberSeriesModuleDropdown));
  }

  saveGeneralNumberSeries(data: any) {
    return this.http.post(this.apiSaveGeneralNumberSeries, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Workflow
  private baseWorkflow = `${this.baseURL}/Workflow`;
  private apiGetRequestModule = `${this.baseWorkflow}/GetRequestModule`;
  private apiCreateWorkflow = `${this.baseWorkflow}/Create`;
  private apiUpdateWorkflow = `${this.baseWorkflow}/Update`;
  private apiWorkflowList = `${this.baseWorkflow}/Get`;
  private apiWorkflowInfo = `${this.baseWorkflow}/GetById`;
  private apiWorkflowTemplate = `${this.baseWorkflow}/GetWorkflowTemplate`;
  private apiWorkflowTemplateInfo = `${this.baseWorkflow}/GetWorkflowTemplateById`;
  private apiCheckWorkflowAction = `${this.baseWorkflow}/CheckWorkflowAction`;
  private apiWorkflowAction = `${this.baseWorkflow}/WorkflowAction`;
  private apiWorkflowExport = `${this.baseWorkflow}/Export`;

  getRequestModule(mainModuleId: number) {
    return this.cache.get(`apiGetRequestModule=${mainModuleId}`, () =>
      this.http.get(this.apiGetRequestModule, { params: { mainModuleId } })
    );
  }

  createWorkflow(data: any) {
    return this.http.post(this.apiCreateWorkflow, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  updateWorkflow(data: any) {
    return this.http.post(this.apiUpdateWorkflow, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getWorkflowList(data: any) {
    return this.http.post<any>(this.apiWorkflowList, data);
  }

  getWorkflowInfoById(id: number) {
    return this.cache.get(`apiWorkflowInfo=${id}`, () => this.http.get(this.apiWorkflowInfo, { params: { id } }));
  }

  getWorkflowTemplateList(searchValue: string) {
    return this.http.post<any>(this.apiWorkflowTemplate, JSON.stringify(searchValue), {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  getWorkflowTemplateById(id: number) {
    return this.cache.get(`apiWorkflowTemplateInfo=${id}`, () => this.http.get(this.apiWorkflowTemplateInfo, { params: { id } }));
  }

  checkWorkflowAction(id: number, actionType: 'activate' | 'deactivate' | 'delete') {
    const data = {
      id: id,
      isActivate: actionType === 'activate' ? true : false,
      isDeactivate: actionType === 'deactivate' ? true : false,
      isDelete: actionType === 'delete' ? true : false
    };
    return this.http.post<any>(this.apiCheckWorkflowAction, data);
  }

  execWorkflowAction(id: number, actionType: 'activate' | 'deactivate' | 'delete') {
    const data = {
      id: id,
      isActivate: actionType === 'activate' ? true : false,
      isDeactivate: actionType === 'deactivate' ? true : false,
      isDelete: actionType === 'delete' ? true : false
    };
    return this.http.post<any>(this.apiWorkflowAction, data);
  }

  exportWorkflow(data: SearchModel) {
    return this.fileService.export(this.apiWorkflowExport, data, 'workflow_').pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  //#region Credit Term
  private apiCreditTermGet = `${this.baseURL}/CreditTerm/Get`;
  private apiCreditTermGetById = `${this.baseURL}/CreditTerm/GetById`;
  private apiCreditTermCreate = `${this.baseURL}/CreditTerm/Create`;
  private apiCreditTermUpdate = `${this.baseURL}/CreditTerm/Update`;
  private apiCreditTermDelete = `${this.baseURL}/CreditTerm/Delete`;
  private apiCreditTermActivityLog = `${this.baseURL}/CreditTerm/GetActivityLog`;
  private apiCreditTermDuplicate = `${this.baseURL}/CreditTerm/Duplicate`;
  private apiCreditTermDropdown = `${this.baseURL}/CreditTerm/GetCreditTermDropdown`;

  GetCreditTerm(data: any) {
    return this.http.post<any>(this.apiCreditTermGet, data);
  }

  GetCreditTermById(id: number) {
    return this.cache.get(`apiCreditTermGetById=${id}`, () => this.http.get<any>(this.apiCreditTermGetById, { params: { id } }));
  }

  GetCTDropdown() {
    return this.cache.get(`apiCreditTermDropdown`, () => this.http.get<any>(this.apiCreditTermDropdown));
  }

  CreateCreditTerm(data: any) {
    return this.http.post<any>(this.apiCreditTermCreate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  UpdateCreditTerm(data: any) {
    return this.http.post<any>(this.apiCreditTermUpdate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  DeleteCreditTerm(data: any) {
    return this.http.post<any>(this.apiCreditTermDelete, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  DuplicateCreditTerm(id: number) {
    return this.http
      .post<any>(this.apiCreditTermDuplicate, null, {
        params: { id: id }
      })
      .pipe(
        tap(() => {
          this.cache.clear();
        })
      );
  }

  GetCreditTermActivityLog(id: number) {
    return this.cache.get(`apiCreditTermActivityLog=${id}`, () => this.http.get<any>(this.apiCreditTermActivityLog, { params: { id } }));
  }

  //#region Payment Term
  private apiPaymentTermGet = `${this.baseURL}/PaymentTerm/Get`;
  private apiPaymentTermGetById = `${this.baseURL}/PaymentTerm/GetById`;
  private apiPaymentTermCreate = `${this.baseURL}/PaymentTerm/Create`;
  private apiPaymentTermUpdate = `${this.baseURL}/PaymentTerm/Update`;
  private apiPaymentTermDelete = `${this.baseURL}/PaymentTerm/Delete`;
  private apiPaymentTermActivityLog = `${this.baseURL}/PaymentTerm/GetActivityLog`;
  private apiPaymentTermDuplicate = `${this.baseURL}/PaymentTerm/Duplicate`;
  private apiPaymentTermDropdown = `${this.baseURL}/PaymentTerm/GetPaymentTermDropdown`;

  GetPaymentTerm(data: any) {
    return this.http.post<any>(this.apiPaymentTermGet, data);
  }

  GetPaymentTermById(id: number) {
    return this.cache.get(`apiPaymentTermGetById=${id}`, () => this.http.get<any>(this.apiPaymentTermGetById, { params: { id } }));
  }

  GetPTDropdown() {
    return this.cache.get(`apiPaymentTermDropdown`, () => this.http.get<any>(this.apiPaymentTermDropdown));
  }

  CreatePaymentTerm(data: any) {
    return this.http.post<any>(this.apiPaymentTermCreate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  UpdatePaymentTerm(data: any) {
    return this.http.post<any>(this.apiPaymentTermUpdate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  DeletePaymentTerm(data: any) {
    return this.http.post<any>(this.apiPaymentTermDelete, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  DuplicatePaymentTerm(id: number) {
    return this.http
      .post<any>(this.apiPaymentTermDuplicate, null, {
        params: { id: id }
      })
      .pipe(
        tap(() => {
          this.cache.clear();
        })
      );
  }

  GetPaymentTermActivityLog(id: number) {
    return this.cache.get(`apiPaymentTermActivityLog=${id}`, () => this.http.get<any>(this.apiPaymentTermActivityLog, { params: { id } }));
  }

  //#region Risk Tier
  private apiRiskGet = `${this.baseURL}/RiskTier/Get`;
  private apiRiskGetById = `${this.baseURL}/RiskTier/GetById`;
  private apiRiskCreate = `${this.baseURL}/RiskTier/Create`;
  private apiRiskUpdate = `${this.baseURL}/RiskTier/Update`;
  private apiRiskDelete = `${this.baseURL}/RiskTier/Delete`;
  private apiRiskActLog = `${this.baseURL}/RiskTier/GetActivityLog`;
  private apiRiskDuplicate = `${this.baseURL}/RiskTier/Duplicate`;
  private apiRiskDropdown = `${this.baseURL}/RiskTier/GetRiskTierDropdown`;

  GetRisk(data: any) {
    return this.http.post<any>(this.apiRiskGet, data);
  }

  GetRiskById(id: number) {
    return this.cache.get(`apiRiskGetById=${id}`, () => this.http.get<any>(this.apiRiskGetById, { params: { id } }));
  }

  GetRiskDropdown() {
    return this.cache.get(`apiRiskDropdown`, () => this.http.get<any>(this.apiRiskDropdown));
  }

  CreateRisk(data: any) {
    return this.http.post<any>(this.apiRiskCreate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  UpdateRisk(data: any) {
    return this.http.post<any>(this.apiRiskUpdate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  DeleteRisk(data: any) {
    return this.http.post<any>(this.apiRiskDelete, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  GetActivityLogRisk(id: number) {
    return this.cache.get(`apiRiskActLog=${id}`, () => this.http.get<any>(this.apiRiskActLog, { params: { id } }));
  }

  DuplicateRisk(id: number) {
    return this.http
      .post<any>(this.apiRiskDuplicate, null, {
        params: { id: id }
      })
      .pipe(
        tap(() => {
          this.cache.clear();
        })
      );
  }

  //#region Blacklist
  private apiBlackGet = `${this.baseURL}/BlacklistReason/Get`;
  private apiBlackGetById = `${this.baseURL}/BlacklistReason/GetById`;
  private apiBlackCreate = `${this.baseURL}/BlacklistReason/Create`;
  private apiBlackUpdate = `${this.baseURL}/BlacklistReason/Update`;
  private apiBlackDelete = `${this.baseURL}/BlacklistReason/Delete`;
  private apiBlackActLog = `${this.baseURL}/BlacklistReason/GetActivityLog`;
  private apiBlackDuplicate = `${this.baseURL}/BlacklistReason/Duplicate`;
  private apiBlackDropdown = `${this.baseURL}/BlacklistReason/GetBlacklistReasonDropdown`;

  GetBlack(data: any) {
    return this.http.post<any>(this.apiBlackGet, data);
  }

  GetBlackById(id: number) {
    return this.cache.get(`apiBlackGetById=${id}`, () => this.http.get<any>(this.apiBlackGetById, { params: { id } }));
  }

  GetBlacklistReasonDropdown() {
    return this.cache.get(`apiBlackDropdown`, () => this.http.get<any>(this.apiBlackDropdown));
  }

  CreateBlack(data: any) {
    return this.http.post<any>(this.apiBlackCreate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  UpdateBlack(data: any) {
    return this.http.post<any>(this.apiBlackUpdate, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  DeleteBlack(data: any) {
    return this.http.post<any>(this.apiBlackDelete, data).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  GetActivityLogBlack(id: number) {
    return this.cache.get(`apiBlackActLog=${id}`, () => this.http.get<any>(this.apiBlackActLog, { params: { id } }));
  }

  DuplicateBlack(id: number) {
    return this.http
      .post<any>(this.apiBlackDuplicate, null, {
        params: { id: id }
      })
      .pipe(
        tap(() => {
          this.cache.clear();
        })
      );
  }
}
